import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Package, MessageSquare, LogOut, Eye, EyeOff, Edit2, X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotes');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    item_code: '',
    size: '',
    design: 'POS Cups™',
    case_quantity: '',
    quantity_detail: '',
    box_dimensions: '',
    case_weight: '',
    cases_per_pallet: '',
    upc: '',
    price_per_case: 0,
    category: 'Hot Cups',
    image_url: '',
    images: [],
    visible: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Check admin auth
    if (localStorage.getItem('admin_auth') !== 'true') {
      navigate('/admin');
      return;
    }
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [quotesRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products`).catch(() => ({ data: [] }))
      ]);
      setQuotes(quotesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const toggleProductVisibility = async (productId) => {
    try {
      const response = await axios.patch(`${API}/admin/products/${productId}/visibility`);
      toast.success(response.data.visible ? 'Product is now visible on site' : 'Product hidden from site');
      
      // Refresh products
      fetchData();
    } catch (error) {
      console.error('Error toggling product visibility:', error);
      toast.error('Failed to update product visibility');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct({...product});
    setShowEditModal(true);
  };

  const handleUpdateProduct = async () => {
    try {
      await axios.patch(`${API}/admin/products/${editingProduct.id}`, editingProduct);
      toast.success('Product updated successfully');
      setShowEditModal(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  const handleAddImage = () => {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      setEditingProduct({
        ...editingProduct,
        images: [...(editingProduct.images || []), imageUrl],
        image_url: editingProduct.image_url || imageUrl
      });
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = editingProduct.images.filter((_, i) => i !== index);
    setEditingProduct({
      ...editingProduct,
      images: newImages,
      image_url: newImages[0] || null
    });
  };

  const handleAddNewProduct = () => {
    setNewProduct({
      name: '',
      item_code: '',
      size: '',
      design: 'POS Cups™',
      case_quantity: '',
      quantity_detail: '',
      box_dimensions: '',
      case_weight: '',
      cases_per_pallet: '',
      upc: '',
      price_per_case: 0,
      category: 'Hot Cups',
      image_url: '',
      images: [],
      visible: true
    });
    setShowAddModal(true);
  };

  const handleCreateProduct = async () => {
    // Validation
    if (!newProduct.name || !newProduct.item_code || !newProduct.size) {
      toast.error('Please fill in Name, Item Code, and Size');
      return;
    }

    try {
      // Generate product ID from item code
      const productId = newProduct.item_code.toLowerCase().replace('pos-hc-', 'pos-hc-');
      
      const productData = {
        ...newProduct,
        id: productId,
        size: parseInt(newProduct.size),
        case_quantity: parseInt(newProduct.case_quantity) || 0,
        cases_per_pallet: parseInt(newProduct.cases_per_pallet) || 0,
        price_per_case: parseFloat(newProduct.price_per_case) || 0,
        image_url: newProduct.images[0] || newProduct.image_url || null
      };

      await axios.post(`${API}/admin/products`, productData);
      toast.success('Product created successfully');
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleAddImageToNew = () => {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      setNewProduct({
        ...newProduct,
        images: [...newProduct.images, imageUrl],
        image_url: newProduct.image_url || imageUrl
      });
    }
  };

  const handleRemoveImageFromNew = (index) => {
    const images = newProduct.images.filter((_, i) => i !== index);
    setNewProduct({
      ...newProduct,
      images: images,
      image_url: images[0] || ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">DC PAC Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Manage your B2B eCommerce platform</p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Quote Requests</p>
                <p className="text-3xl font-bold text-gray-900">{quotes.length}</p>
              </div>
              <MessageSquare className="h-12 w-12 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Products (Visible/Total)</p>
                <p className="text-3xl font-bold text-gray-900">
                  {products.filter(p => p.visible).length}/{products.length}
                </p>
              </div>
              <Package className="h-12 w-12 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" data-testid="admin-tabs">
              <button
                onClick={() => setActiveTab('quotes')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'quotes'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="quotes-tab"
              >
                Quote Requests ({quotes.length})
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'products'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="products-tab"
              >
                Products ({products.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'quotes' && (
              <div className="space-y-4" data-testid="quotes-content">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Quote Requests</h2>
                {quotes.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No quote requests yet</p>
                ) : (
                  quotes.map((quote) => (
                    <div key={quote.id} className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200" data-testid={`quote-${quote.id}`}>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Contact Person</p>
                          <p className="font-semibold text-gray-900">{quote.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Company</p>
                          <p className="font-semibold text-gray-900">{quote.company}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-semibold text-gray-900">{quote.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-semibold text-gray-900">{quote.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Message</p>
                        <div className="bg-white p-4 rounded border border-gray-300">
                          <p className="text-gray-800">{quote.message}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {new Date(quote.created_at).toLocaleString()}
                        </span>
                        <span className={`badge ${quote.status === 'pending' ? 'badge-gray' : 'badge-green'}`}>
                          {quote.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-4" data-testid="products-content">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Product Management</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {products.filter(p => p.visible).length} of {products.length} products visible on site
                    </p>
                  </div>
                  <Button 
                    onClick={handleAddNewProduct}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Product
                  </Button>
                </div>
                {products.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No products found</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {products.map((product) => (
                      <div key={product.id} className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200" data-testid={`product-${product.id}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                            <p className="text-sm text-gray-600">Item Code: {product.item_code}</p>
                          </div>
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-16 h-16 object-contain rounded border border-gray-300"
                            />
                          )}
                        </div>
                        
                        <div className="space-y-2 mb-4 text-sm">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-gray-600">Case Quantity</p>
                              <p className="font-semibold">{product.case_quantity}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Packaging</p>
                              <p className="font-semibold text-xs">{product.quantity_detail}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-600">Box Dimensions</p>
                            <p className="font-semibold text-xs">{product.box_dimensions}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-gray-600">Case Weight</p>
                              <p className="font-semibold">{product.case_weight}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Cases per Pallet</p>
                              <p className="font-semibold">{product.cases_per_pallet}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-600">UPC</p>
                            <p className="font-semibold text-xs">{product.upc}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-300 gap-2">
                          <div className="flex items-center">
                            {product.visible ? (
                              <>
                                <Eye className="h-4 w-4 text-green-600 mr-2" />
                                <span className="text-sm font-semibold text-green-600">Visible</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm font-semibold text-gray-400">Hidden</span>
                              </>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleEditProduct(product)}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              onClick={() => toggleProductVisibility(product.id)}
                              variant={product.visible ? "outline" : "default"}
                              size="sm"
                              className={product.visible ? "text-red-600 border-red-300 hover:bg-red-50" : "bg-green-600 hover:bg-green-700 text-white"}
                              data-testid={`toggle-visibility-${product.id}`}
                            >
                              {product.visible ? (
                                <>
                                  <EyeOff className="h-4 w-4 mr-1" />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-1" />
                                  Show
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Edit Product: {editingProduct.name}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Images */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Product Images</label>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {(editingProduct.images || []).map((img, index) => (
                    <div key={index} className="relative group">
                      <img src={img} alt={`Product ${index + 1}`} className="w-full h-24 object-contain border-2 border-gray-300 rounded" />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button onClick={handleAddImage} variant="outline" size="sm" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Image URL
                </Button>
              </div>

              {/* 6 Editable Parameters */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Case Quantity</label>
                  <input
                    type="number"
                    value={editingProduct.case_quantity}
                    onChange={(e) => setEditingProduct({...editingProduct, case_quantity: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Packaging</label>
                  <input
                    type="text"
                    value={editingProduct.quantity_detail}
                    onChange={(e) => setEditingProduct({...editingProduct, quantity_detail: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="e.g., 20 x 50 Cups"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Box Dimensions</label>
                  <input
                    type="text"
                    value={editingProduct.box_dimensions}
                    onChange={(e) => setEditingProduct({...editingProduct, box_dimensions: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder='e.g., 17.88" x 14.25" x 18.25"'
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Case Weight</label>
                  <input
                    type="text"
                    value={editingProduct.case_weight}
                    onChange={(e) => setEditingProduct({...editingProduct, case_weight: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="e.g., 24.2 lbs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cases per Pallet</label>
                  <input
                    type="number"
                    value={editingProduct.cases_per_pallet}
                    onChange={(e) => setEditingProduct({...editingProduct, cases_per_pallet: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">UPC</label>
                  <input
                    type="text"
                    value={editingProduct.upc}
                    onChange={(e) => setEditingProduct({...editingProduct, upc: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button onClick={() => setShowEditModal(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleUpdateProduct} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Add New Product</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Basic Information</h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Product Name *</label>
                    <input
                      type="text"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., POS Cups™ 8 oz"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Item Code *</label>
                    <input
                      type="text"
                      value={newProduct.item_code}
                      onChange={(e) => setNewProduct({...newProduct, item_code: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., POS-HC-08"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Size (oz) *</label>
                    <input
                      type="number"
                      value={newProduct.size}
                      onChange={(e) => setNewProduct({...newProduct, size: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 8"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Design</label>
                    <input
                      type="text"
                      value={newProduct.design}
                      onChange={(e) => setNewProduct({...newProduct, design: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Product Images */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Product Images</label>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {newProduct.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img src={img} alt={`Product ${index + 1}`} className="w-full h-24 object-contain border-2 border-gray-300 rounded" />
                      <button
                        onClick={() => handleRemoveImageFromNew(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button onClick={handleAddImageToNew} variant="outline" size="sm" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Image URL
                </Button>
              </div>

              {/* Specifications */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Specifications</h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Case Quantity</label>
                    <input
                      type="number"
                      value={newProduct.case_quantity}
                      onChange={(e) => setNewProduct({...newProduct, case_quantity: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Packaging</label>
                    <input
                      type="text"
                      value={newProduct.quantity_detail}
                      onChange={(e) => setNewProduct({...newProduct, quantity_detail: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 20 x 50 Cups"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Box Dimensions</label>
                    <input
                      type="text"
                      value={newProduct.box_dimensions}
                      onChange={(e) => setNewProduct({...newProduct, box_dimensions: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder='e.g., 17.88" x 14.25" x 18.25"'
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Case Weight</label>
                    <input
                      type="text"
                      value={newProduct.case_weight}
                      onChange={(e) => setNewProduct({...newProduct, case_weight: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 24.2 lbs"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cases per Pallet</label>
                    <input
                      type="number"
                      value={newProduct.cases_per_pallet}
                      onChange={(e) => setNewProduct({...newProduct, cases_per_pallet: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 42"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">UPC</label>
                    <input
                      type="text"
                      value={newProduct.upc}
                      onChange={(e) => setNewProduct({...newProduct, upc: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="e.g., 850068606023"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button onClick={() => setShowAddModal(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleCreateProduct} className="bg-green-600 hover:bg-green-700 text-white">
                  Create Product
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
